/**
 * ────────────────────────────────────────────────────────────────────────────
 * CAPA DE DETECCIÓN DE PLATAFORMA — Capacitor.isNativePlatform()
 * ────────────────────────────────────────────────────────────────────────────
 * Un solo punto de verdad para "¿estoy corriendo dentro del shell nativo
 * (WebView de iOS/Android) o en un navegador normal (PWA)?". Todo el resto de
 * `src/lib/native/*` (geolocation, push, camera) rama sobre esto: nativo usa el
 * plugin de Capacitor, web cae a la API del navegador — la PWA sigue
 * funcionando igual en Chrome/Safari de escritorio o mobile.
 *
 * SSR-SAFE: `@capacitor/core` resuelve `getPlatform()` contra `globalThis`, no
 * contra `window` directo, así que importarlo en un Server Component / durante
 * el render SSR de Next NO revienta (Node no tiene `window`, pero sí
 * `globalThis`, y `defaultGetPlatform` cae a "web" con seguridad). En el
 * navegador real (o la WebView nativa) el resultado es el correcto: en la
 * WebView, el bridge nativo inyecta `window.webkit.messageHandlers.bridge`
 * (iOS) o `window.androidBridge` (Android) ANTES de que la página cargue, así
 * que `isNativePlatform()` ya da `true` en el primer render del cliente.
 *
 * OJO CON HIDRATACIÓN: el server SIEMPRE ve "web" (false). Si vas a decidir qué
 * renderizar en un componente cliente según esto, usá el hook
 * `useIsNativePlatform()` de más abajo (no `isNativePlatform()` directo en el
 * cuerpo del render): usa `useSyncExternalStore` con `getServerSnapshot=()=>
 * false`, que es el patrón que React documenta para "valor que sólo se conoce
 * en el cliente y puede diferir del snapshot de server" — evita el warning de
 * hydration mismatch SIN el anti-patrón de `setState` síncrono dentro de un
 * `useEffect` (que dispara la regla de lint `react-hooks/set-state-in-effect`).
 * Ver el botón de cámara nativa en `OnboardingWizard.tsx` para el uso.
 */
import { Capacitor } from "@capacitor/core";
import { useSyncExternalStore } from "react";

export type NativePlatform = "ios" | "android" | "web";

/** `true` sólo dentro del shell nativo (WebView de Capacitor), nunca en web/PWA. */
export function isNativePlatform(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    // Defensivo: si algún entorno raro (SSR exótico, test runner) hace que
    // Capacitor tire, tratamos como web — nunca dejamos que esto rompa el render.
    return false;
  }
}

/** "ios" | "android" | "web". Nunca lanza. */
export function nativePlatform(): NativePlatform {
  try {
    const platform = Capacitor.getPlatform();
    return platform === "ios" || platform === "android" ? platform : "web";
  } catch {
    return "web";
  }
}

/**
 * ¿Está registrado el plugin nativo `name` en esta build? Útil para
 * feature-detect fino (p.ej. un plugin community que no siempre está
 * instalado) sin tener que envolver cada call-site en try/catch propio.
 */
export function isNativePluginAvailable(name: string): boolean {
  try {
    return Capacitor.isPluginAvailable(name);
  } catch {
    return false;
  }
}

// El valor nunca cambia en caliente (no hay evento "te convertiste en nativo"
// a mitad de sesión), así que el store nunca notifica — sólo existe para que
// `useSyncExternalStore` sepa distinguir el snapshot de server del de cliente.
const noopSubscribe = () => () => {};
const getServerSnapshot = () => false;

/**
 * Hook para componentes cliente: `false` en el render de hidratación (igual
 * que el server) y, si estamos en el shell nativo, `true` desde el primer
 * render post-hidratación — sin hydration mismatch y sin la cascada de
 * re-render de un `useEffect` + `setState`. Ver la nota de arriba.
 */
export function useIsNativePlatform(): boolean {
  return useSyncExternalStore(noopSubscribe, isNativePlatform, getServerSnapshot);
}
